import { ReactNode } from 'react';
import { pipe } from 'fp-ts/function';
import styled from 'styled-components';
import { array, nonEmptyArray, boolean, ord, number } from 'fp-ts';
import { primitiveTypeUniq } from '@mews/fp';
import { LocalizedEnum, lt } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { Icon } from '@optimus-web/core';
import {
    NewRoutingRule,
    OrderItemType,
    ReservationUpdateRoutingDetails,
    RoutedItemsDetails,
    Service,
} from '@mews-commander/data-access';
import { IconName } from '@optimus-web/icons';
import { SectionTag, SectionType, SubSectionLine } from '@mews-commander/ui';
import { AdditionalService, BookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { createHandler } from '@mews/std';
import { TextKey } from '@mews-commander/enums';
import { BillingAutomationChangesOrigin, BillingAutomationChangesType } from '@mews-commander/utils';
import { AutomationTag } from '../types';
import { ServiceTimeUnit } from '../../../../components';
import { getTargetDescription } from '../../utils/targetBill';
import { AffectedReservations } from './AffectedReservations';

const getRoutingRuleName = (routingRule?: NewRoutingRule | null) => {
    if (!routingRule) {
        return lt('BillingAutomation');
    }

    return (
        routingRule.name || (
            <>
                {routingRule.companiesWithRelations[0].companyName} {lt('BillingAutomation')}
            </>
        )
    );
};

const getDestinationLines = (reservationRoutingDetails: ReservationUpdateRoutingDetails[]): SubSectionLine[] => {
    const { assignmentTargetType } = reservationRoutingDetails[0].newRoutingRule || {};
    const { billAggregationType } = reservationRoutingDetails[0].newRoutingRule || {};

    return [
        {
            id: 'target-bill',
            icon: IconName.BillsInvoices,
            name: lt('TargetBill'),
            strong: true,
            items: [{ name: reservationRoutingDetails[0].ownerName }],
        },
        ...(assignmentTargetType && billAggregationType
            ? [getTargetDescription(assignmentTargetType, billAggregationType)]
            : []),
    ];
};

const getTotalUnRoutedItemsCount = (reservationRoutingDetails: ReservationUpdateRoutingDetails[]) =>
    reservationRoutingDetails.reduce((acc, r) => acc + r.unRoutedItemsCount, 0);

const getUniqueItemTypesByServiceId = (routedOrUnroutedItemsDetails: RoutedItemsDetails[]): RoutedItemsDetails[] =>
    pipe(
        routedOrUnroutedItemsDetails.reduce((acc: Record<string, RoutedItemsDetails>, item) => {
            if (acc[item.serviceId]) {
                acc[item.serviceId].itemTypes.push(...item.itemTypes);
            } else {
                acc[item.serviceId] = { ...item };
            }

            return acc;
        }, {}),
        Object.values,
        array.map((itemDetail) => ({ ...itemDetail, itemTypes: pipe(itemDetail.itemTypes, primitiveTypeUniq) }))
    );

const sortByBookableOrAdditionalAndServiceOrdering = (
    routedOrUnroutedItemsDetails: RoutedItemsDetails[],
    allServices: { additionalServices: AdditionalService[]; bookableServices: BookableService[] }
): RoutedItemsDetails[] => {
    const { bookableServices, additionalServices } = allServices;
    const servicesMap = [...bookableServices, ...additionalServices].reduce<Record<string, Service>>((acc, service) => {
        acc[service.id] = service;
        return acc;
    }, {});
    const additionalServicesIds = additionalServices.map((v) => v.id);

    return pipe(
        routedOrUnroutedItemsDetails,
        array.sortBy([
            pipe(
                boolean.Ord,
                ord.contramap(({ serviceId }: RoutedItemsDetails) => additionalServicesIds.includes(serviceId))
            ),
            pipe(
                number.Ord,
                ord.contramap(({ serviceId }: RoutedItemsDetails) => servicesMap[serviceId]?.ordering || 0)
            ),
        ])
    );
};

const getRoutingRuleItemType = (itemType: OrderItemType, serviceId: string): ReactNode => {
    if (itemType === OrderItemType.SpaceOrder) {
        return <ServiceTimeUnit serviceId={serviceId} />;
    }

    return <LocalizedEnum value={itemType} prefix="OrderItemType" />;
};

const getRoutingRuleItemTypes = (
    routedOrUnRoutedItemsDetails: RoutedItemsDetails[],
    allServices: { additionalServices: AdditionalService[]; bookableServices: BookableService[] }
): SubSectionLine[] =>
    pipe(
        routedOrUnRoutedItemsDetails,
        getUniqueItemTypesByServiceId,
        (routedItemDetails: RoutedItemsDetails[]) =>
            sortByBookableOrAdditionalAndServiceOrdering(routedItemDetails, allServices),
        array.map((itemDetail) => ({
            id: itemDetail.serviceId,
            name: itemDetail.serviceName,
            items: itemDetail.itemTypes.map((itemType: OrderItemType) => ({
                name: getRoutingRuleItemType(itemType, itemDetail.serviceId),
            })),
            compact: true,
        }))
    );

const getRoutingRuleAffectedReservations = (
    reservationRoutingDetails: ReservationUpdateRoutingDetails[]
): SubSectionLine[] => {
    if (reservationRoutingDetails.length <= 1) {
        return [];
    }

    return [
        {
            name: lt('RoutingRuleChangeConfirmationPanelAffectedReservations'),
            icon: IconName.SummaryOrder,
            badge: reservationRoutingDetails.length.toString(),
            badgeVariant: 'subtle',
            strong: true,
            expandableContent: (
                <AffectedReservations reservationIds={reservationRoutingDetails.map((r) => r.reservationId)} />
            ),
        },
    ];
};

export const getAutomationTagProps = createHandler<AutomationTag, SectionTag>({
    [AutomationTag.CurrentAutomation]: { variant: 'basicSubtle', textKey: TextKey.RoutingRulesCurrentAutomation },
    [AutomationTag.NewAutomation]: { variant: 'primary', textKey: TextKey.RoutingRulesNewAutomation },
});

const getUnRoutedRoutingRulesSection = (
    currentRoutingRulesWithUnRoutedItems: ReservationUpdateRoutingDetails[][],
    allServices: { additionalServices: AdditionalService[]; bookableServices: BookableService[] }
): SectionType => ({
    id: 'current-automation-section',
    subSections: currentRoutingRulesWithUnRoutedItems.map((r, index) => ({
        id: `current-automation-sub-section-${index}`,
        title: getRoutingRuleName(r[0].currentRoutingRule),
        tag: getAutomationTagProps(AutomationTag.CurrentAutomation),
        helpText: lt('RoutingRuleItemsMovedToOwnerUnpaidTooltip'),
        lines: [
            {
                id: 'items-returned-to-owner',
                icon: IconName.Restore,
                name: lt('RoutingRuleItemsMovedToOwnerUnpaid'),
                strong: true,
                badge: getTotalUnRoutedItemsCount(r).toString(),
                badgeVariant: 'subtle',
            },
            ...getRoutingRuleItemTypes(
                r.flatMap((rD) => rD.unRoutedItems),
                allServices
            ),
            ...getRoutingRuleAffectedReservations(r),
        ],
    })),
    dividerType: 'arrow',
});

const getTotalRoutedItemsCount = (reservationRoutingDetails: ReservationUpdateRoutingDetails[]) =>
    reservationRoutingDetails.reduce((acc, r) => acc + r.routedItemsCount, 0);

const getRoutedRoutingRulesSection = (
    newRoutingRulesWithRoutedItems: ReservationUpdateRoutingDetails[][],
    allServices: { additionalServices: AdditionalService[]; bookableServices: BookableService[] }
): SectionType => ({
    id: 'new-automation-section',
    subSections: newRoutingRulesWithRoutedItems.map((r, index) => ({
        id: `new-automation-sub-section-${index}`,
        title: getRoutingRuleName(r[0].newRoutingRule),
        tag: getAutomationTagProps(AutomationTag.NewAutomation),

        helpText: lt('RoutingRuleItemsMovedFromOwnerUnpaidToNewBillsTooltip'),
        lines: [
            {
                id: 'items-returned-to-owner',
                icon: IconName.Items,
                name: lt('RoutingRuleItemsMovedFromOwnerUnpaidToNewBills'),
                strong: true,
                badge: getTotalRoutedItemsCount(r).toString(),
                badgeVariant: 'subtle',
            },
            ...getRoutingRuleItemTypes(
                r.flatMap((rD) => rD.routedItems),
                allServices
            ),
            ...getRoutingRuleAffectedReservations(r),
            ...getDestinationLines(r),
        ],
    })),
});

export const getTotalItemsMoved = (reservationRoutingDetails: ReservationUpdateRoutingDetails[]) =>
    reservationRoutingDetails.reduce((acc, r) => acc + r.routedItemsCount + r.unRoutedItemsCount, 0);

const IconAligned = styled(Icon)`
    vertical-align: middle;
`;

export const getBanner = (
    reservationRoutingDetails: ReservationUpdateRoutingDetails[]
): { title: ReactNode; subtitle?: ReactNode } => {
    const isSingleReservationChange = reservationRoutingDetails.length === 1;

    if (isSingleReservationChange) {
        if (
            !reservationRoutingDetails[0].currentRoutingRule ||
            reservationRoutingDetails[0].currentRoutingRule?.id === reservationRoutingDetails[0].newRoutingRule?.id
        ) {
            return {
                title: lt('RoutingRuleApplyToReservationConfirmationPanelBanner', {
                    name: getRoutingRuleName(reservationRoutingDetails[0].newRoutingRule),
                }),
            };
        }

        if (!reservationRoutingDetails[0].newRoutingRule) {
            return {
                title: lt('RoutingRuleRemoveFromReservationConfirmationPanelBanner', {
                    name: getRoutingRuleName(reservationRoutingDetails[0].currentRoutingRule),
                }),
            };
        }

        return {
            title: <>{lt('RoutingRuleReplaceToReservationConfirmationPanelBanner')}</>,
            subtitle: (
                <>
                    {getRoutingRuleName(reservationRoutingDetails[0].currentRoutingRule)}
                    {UnicodeEntity.NoBreakSpace}
                    <IconAligned size="small" value={IconName.ArrowRight} />
                    {UnicodeEntity.NoBreakSpace}
                    {getRoutingRuleName(reservationRoutingDetails[0].newRoutingRule)}
                </>
            ),
        };
    }

    return {
        title: lt('RoutingRuleMoveBillingItemsOnMultipleReservationsConfirmationPanelBanner'),
    };
};

export interface RoutingConfirmationData {
    sections: SectionType[];
    items: number;
    banner: { title: ReactNode; subtitle?: ReactNode };
    typeOfChange: BillingAutomationChangesType;
}
export const mapReservationRoutingDetailsToConfirmationPanel = (
    reservationRoutingDetails: ReservationUpdateRoutingDetails[],
    allServices: { additionalServices: AdditionalService[]; bookableServices: BookableService[] }
): RoutingConfirmationData => {
    const currentRoutingRulesWithUnRoutedItems: ReservationUpdateRoutingDetails[][] = pipe(
        reservationRoutingDetails,
        array.filter((r) => !!r.currentRoutingRule && r.unRoutedItemsCount > 0),
        nonEmptyArray.groupBy((r) => r.currentRoutingRule?.id ?? ''),
        Object.values
    );

    const newRoutingRulesWithRoutedItems: ReservationUpdateRoutingDetails[][] = pipe(
        reservationRoutingDetails,
        array.filter((r) => !!r.newRoutingRule && r.routedItemsCount > 0),
        nonEmptyArray.groupBy((r) => r.newRoutingRule?.id ?? ''),
        Object.values
    );

    const sections = [];

    const currentRoutingRules: ReservationUpdateRoutingDetails[][] = pipe(
        reservationRoutingDetails,
        array.filter((r) => !!r.currentRoutingRule),
        nonEmptyArray.groupBy((r) => r.currentRoutingRule?.id ?? ''),
        Object.values
    );

    const isSingleReservationChange = reservationRoutingDetails.length === 1;

    if (isSingleReservationChange) {
        if (currentRoutingRulesWithUnRoutedItems.length > 0) {
            sections.push(getUnRoutedRoutingRulesSection(currentRoutingRulesWithUnRoutedItems, allServices));
        }
    } else if (currentRoutingRules.length > 0) {
        sections.push(getUnRoutedRoutingRulesSection(currentRoutingRules, allServices));
    }

    if (newRoutingRulesWithRoutedItems.length > 0) {
        sections.push(getRoutedRoutingRulesSection(newRoutingRulesWithRoutedItems, allServices));
    }

    return {
        sections,
        items: getTotalItemsMoved(reservationRoutingDetails),
        banner: getBanner(reservationRoutingDetails),
        typeOfChange: getRoutingRuleChanges(currentRoutingRules, newRoutingRulesWithRoutedItems),
    };
};

export const reservationHasRoutingRuleChanges = (reservationRoutingDetails: ReservationUpdateRoutingDetails[]) =>
    reservationRoutingDetails.some((r) => r.routedItemsCount > 0 || r.unRoutedItemsCount > 0);

const getRoutingRuleChanges = (
    currentRoutingRules: ReservationUpdateRoutingDetails[][],
    newRoutingRulesWithRoutedItems: ReservationUpdateRoutingDetails[][]
): BillingAutomationChangesType => {
    if (newRoutingRulesWithRoutedItems.length > 0 && currentRoutingRules.length > 0) {
        return 'replaced';
    }
    if (newRoutingRulesWithRoutedItems.length > 0) {
        return 'added';
    }
    return 'removed';
};

/**
 * Determines the origin of the changes based on the provided IDs.
 * @param params - The parameters object containing company and agency IDs.
 * @param params.partnerCompanyId - The ID of the partner company, if applicable.
 * @param params.travelAgencyId - The ID of the travel agency, if applicable.
 * @returns The origin of the changes as a string.
 */
export const getOrigin = ({
    partnerCompanyId,
    travelAgencyId,
}: {
    partnerCompanyId?: string | null;
    travelAgencyId?: string | null;
}): BillingAutomationChangesOrigin => {
    if (partnerCompanyId !== undefined) {
        return 'Company';
    }
    if (travelAgencyId !== undefined) {
        return 'TravelAgency';
    }
    return 'Rates';
};
